import { Thread } from "@/components/assistant-ui/thread";
import { ThreadList } from "@/components/assistant-ui/thread-list";
import { DocumentViewerPanel } from "@/components/frontend/chat/DocumentViewerPanel";
import { FrontendPageContent } from "@/components/frontend/FrontendPageContent";
import { ErrorResetBoundary } from "@/components/shared/ErrorResetBoundary";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@/components/ui/resizable";
import { HOME_ORG_PATH } from "@/config/constants";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { cn } from "@/lib/utils";
import Page404 from "@/pages/404";
import { useAuiState } from "@assistant-ui/react";
import { useEffect } from "react";
import { useLocation } from "react-router";
import { CHAT_PATH, getChatHistoryTab } from "./chatRoutes";
import { ChatFileDropZone } from "./components/ChatFileDropZone";
import { ChatHeader } from "./components/ChatHeader";
import { MarkdownActionsBridge } from "./components/MarkdownActionsProvider";
import { DocumentViewerProvider } from "./contexts/DocumentViewerProvider";
import { useDocumentViewer } from "./contexts/useDocumentViewer";
import { ChatHistoryPanel } from "./history/ChatHistoryPanel";
import {
    useChatRouteThreadId,
    useIsUnknownChatRoute,
} from "./hooks/useChatRouteThreadId";
import { useOpenDocumentFromUrlEffect } from "./hooks/useOpenDocumentFromUrlEffect";

const ChatLayout = ({ isHistory }: { isHistory: boolean }) => {
    const { isOpen } = useDocumentViewer();
    const [sidebarOpen, setSidebarOpen] = useLocalStorage(
        "chat-sidebar-open",
        true
    );
    const { setBreadcrumbs } = useBreadcrumb();
    const threadTitle = useAuiState((s) => {
        const mainId = s.threads.mainThreadId;
        return s.threads.threadItems.find((t) => t.id === mainId)?.title ?? "";
    });

    // Consume `?open_document=<id>&page=<n>` query params on mount --
    // the receiver for hyperlinks emitted into Polly xlsx exports.
    useOpenDocumentFromUrlEffect();

    useEffect(() => {
        setBreadcrumbs(
            isHistory
                ? [
                      { label: "Home", path: HOME_ORG_PATH },
                      { label: "Ask Polly", path: CHAT_PATH },
                      { label: "Chat history" },
                  ]
                : [
                      { label: "Home", path: HOME_ORG_PATH },
                      { label: "Ask Polly" },
                      { label: threadTitle || "New" },
                  ]
        );
    }, [isHistory, threadTitle, setBreadcrumbs]);

    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    return (
        <ChatFileDropZone className="flex h-full min-w-0 flex-1">
            {/* Sidebar collapses via CSS width transition (not unmount) to avoid
               ResizablePanelGroup remounting and losing panel sizes. */}
            <aside
                id="chat-sidebar"
                className={cn(
                    "bg-muted/30 flex shrink-0 flex-col overflow-hidden border-r transition-all duration-200",
                    sidebarOpen ? "w-64" : "w-0 border-r-0"
                )}
            >
                <div className="flex-1 overflow-y-auto px-2 py-3">
                    <ThreadList />
                </div>
            </aside>

            {isHistory ? (
                <ChatHistoryPanel
                    sidebarOpen={sidebarOpen}
                    onToggleSidebar={toggleSidebar}
                />
            ) : (
                <ResizablePanelGroup
                    orientation="horizontal"
                    style={{ flex: 1, minWidth: 0, minHeight: 0 }}
                >
                    <ResizablePanel
                        id="chat-v2-panel"
                        defaultSize="60"
                        minSize={350}
                        style={{
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <ChatHeader
                            sidebarOpen={sidebarOpen}
                            onToggleSidebar={toggleSidebar}
                        />
                        <div className="min-h-0 flex-1">
                            <ErrorResetBoundary>
                                <Thread />
                            </ErrorResetBoundary>
                        </div>
                    </ResizablePanel>
                    {isOpen && (
                        <>
                            <ResizableHandle withHandle />
                            <ResizablePanel
                                id="viewer-v2-panel"
                                defaultSize="40"
                                minSize={320}
                            >
                                <ErrorResetBoundary>
                                    <DocumentViewerPanel />
                                </ErrorResetBoundary>
                            </ResizablePanel>
                        </>
                    )}
                </ResizablePanelGroup>
            )}
        </ChatFileDropZone>
    );
};

const ChatPage = () => {
    const { pathname } = useLocation();
    const isHistory = getChatHistoryTab(pathname) !== null;
    useDocumentTitle(isHistory ? "Chat history" : "Ask Polly");
    const conversationId = useChatRouteThreadId();

    return (
        <FrontendPageContent className="flex h-[calc(100svh-var(--top-offset))]!">
            <DocumentViewerProvider conversationId={conversationId}>
                <MarkdownActionsBridge>
                    <ChatLayout isHistory={isHistory} />
                </MarkdownActionsBridge>
            </DocumentViewerProvider>
        </FrontendPageContent>
    );
};

const Chat = () => (useIsUnknownChatRoute() ? <Page404 /> : <ChatPage />);

export default Chat;
